import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { Goal, Game } from '../types';
import GoalDiagramPDF from './GoalDiagramPDF';
import GoalAnalyticsPDF from './GoalAnalyticsPDF';
import ShotPositionDiagramPDF from './ShotPositionDiagramPDF';
import ShotPositionAnalyticsPDF from './ShotPositionAnalyticsPDF';

interface GameWithGoals extends Game {
  goals: Goal[];
}

interface AnalyticsPDFProps {
  games: GameWithGoals[];
  goals: Goal[];
  dateRange?: { start: string; end: string };
  selectionMode: 'all' | 'single' | 'multiple' | 'date-range';
}

const styles = StyleSheet.create({
  page: {
    padding: 20,
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: 10,
  },
  title: {
    fontSize: 20,
    marginBottom: 5,
    fontWeight: 'bold',
    color: '#1b3968',
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 10,
    color: '#6b7280',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 'bold',
    color: '#1b3968',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 10,
    gap: 20,
  },
  column: {
    flex: 1,
  },
  label: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
  },
  statsContainer: {
    flexDirection: 'row',
    marginBottom: 15,
    gap: 10,
  },
  statBox: {
    flex: 1,
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  visualizationContainer: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
    backgroundColor: '#ffffff',
  },
  visualizationTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#1b3968',
    textAlign: 'center',
  },
  note: {
    fontSize: 8,
    color: '#6b7280',
    marginTop: 6,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f3f4f6',
  },
  tableCell: {
    padding: 5,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    fontSize: 10,
  },
  tableCellHeader: {
    fontWeight: 'bold',
    fontSize: 10,
    color: '#1b3968',
  },
  col1: { width: '25%' },
  col2: { width: '25%' },
  col3: { width: '15%' },
  col4: { width: '15%' },
  col5: { width: '20%' },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#6b7280',
  },
  distributionTable: {
    marginBottom: 20,
  },
  distributionRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 5,
  },
  distributionLabel: {
    flex: 2,
    fontSize: 10,
    color: '#374151',
  },
  distributionValue: {
    flex: 1,
    fontSize: 10,
    color: '#374151',
    textAlign: 'right',
  },
  distributionPercentage: {
    flex: 1,
    fontSize: 10,
    color: '#374151',
    textAlign: 'right',
  },
});

const formatLabel = (label: string) => {
  return label
    .replace(/ Goal$/, '')
    .replace(/ Shot$/, '')
    .replace(' / ', '/');
};

const countGoalTypes = (goals: Goal[], type: 'shotType' | 'gameSituation') => {
  const counts: Record<string, number> = {};
  goals.forEach(goal => {
    const value = goal[type];
    if (value) {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ 
      name: formatLabel(name),
      fullName: name,
      value 
    }))
    .sort((a, b) => b.value - a.value);
};

const AnalyticsPDF: React.FC<AnalyticsPDFProps> = ({ 
  games, 
  goals, 
  dateRange, 
  selectionMode 
}) => {
  const totalGames = games.length;
  const totalShots = games.reduce((acc, game) => acc + (game.shotsFaced || 0), 0);
  const totalSaves = games.reduce((acc, game) => acc + (game.saves || 0), 0);
  const savePercentage = totalShots > 0 ? ((totalSaves / totalShots) * 100).toFixed(1) : '0.0';
  const goalsAgainst = goals.length;
  const averageGoalsPerGame = totalGames > 0 ? (goalsAgainst / totalGames).toFixed(2) : '0.00';

  const sortedGames = [...games].sort((a, b) => b.date.getTime() - a.date.getTime());

  const gamesPerPage = 10;
  const gameChunks = sortedGames.reduce((acc, game, i) => {
    const chunkIndex = Math.floor(i / gamesPerPage);
    if (!acc[chunkIndex]) {
      acc[chunkIndex] = [];
    }
    acc[chunkIndex].push(game);
    return acc;
  }, [] as GameWithGoals[][]);

  const getTitle = () => {
    switch (selectionMode) {
      case 'all':
        return 'All Games Analytics';
      case 'single':
        return 'Single Game Analytics';
      case 'multiple':
        return 'Selected Games Analytics';
      case 'date-range':
        return 'Date Range Analytics';
      default:
        return 'Goalie Analytics';
    }
  };

  const getSubtitle = () => {
    switch (selectionMode) {
      case 'all':
        return `Analysis of all ${totalGames} games`;
      case 'single':
        return games.length > 0 
          ? `Analysis of game against ${games[0].opponent} on ${games[0].date.toLocaleDateString()}`
          : 'No games selected';
      case 'multiple':
        return `Analysis of ${totalGames} selected games`;
      case 'date-range':
        if (dateRange) {
          return `Analysis from ${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}`;
        }
        return `Analysis of ${totalGames} games in date range`;
      default:
        return '';
    }
  };

  const shotTypes = countGoalTypes(goals, 'shotType');
  const gameSituations = countGoalTypes(goals, 'gameSituation');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{getTitle()}</Text>
          <Text style={styles.subtitle}>{getSubtitle()}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Performance Statistics</Text>
          <View style={styles.statsContainer}>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Games</Text>
              <Text style={styles.statValue}>{totalGames}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Shots Faced</Text>
              <Text style={styles.statValue}>{totalShots}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Saves</Text>
              <Text style={styles.statValue}>{totalSaves}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Save %</Text>
              <Text style={styles.statValue}>{savePercentage}%</Text>
            </View>
          </View>
          <View style={styles.statsContainer}>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Goals Against</Text>
              <Text style={styles.statValue}>{goalsAgainst}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Avg. Goals/Game</Text>
              <Text style={styles.statValue}>{averageGoalsPerGame}</Text>
            </View>
            <View style={styles.statBox}>
              <Text style={styles.statLabel}>Goals Recorded</Text>
              <Text style={styles.statValue}>{goals.length}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Goal Locations</Text>
              <GoalDiagramPDF goals={goals} width={230} height={172.5} />
              <Text style={styles.note}>
                Each point represents a goal's location
              </Text>
            </View>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Goal Distribution</Text>
              <GoalAnalyticsPDF goals={goals} width={230} height={172.5} />
              <Text style={styles.note}>
                Percentage of goals scored in each zone
              </Text>
            </View>
          </View>
          <View style={[styles.row, { marginTop: 20 }]}>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Shot Locations</Text>
              <ShotPositionDiagramPDF goals={goals} width={230} height={172.5} />
              <Text style={styles.note}>
                Each point represents where a shot was taken from
              </Text>
            </View>
            <View style={styles.visualizationContainer}>
              <Text style={styles.visualizationTitle}>Shot Distribution</Text>
              <ShotPositionAnalyticsPDF goals={goals} width={230} height={172.5} />
              <Text style={styles.note}>
                Percentage of shots taken from each zone
              </Text>
            </View>
          </View>
        </View>

        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages + gameChunks.length}`} 
          fixed 
        />
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Shot Types Distribution</Text>
          <View style={styles.distributionTable}>
            <View style={[styles.distributionRow, { backgroundColor: '#f3f4f6', paddingVertical: 8 }]}>
              <Text style={[styles.distributionLabel, { fontWeight: 'bold' }]}>Shot Type</Text>
              <Text style={[styles.distributionValue, { fontWeight: 'bold' }]}>Count</Text>
              <Text style={[styles.distributionPercentage, { fontWeight: 'bold' }]}>Percentage</Text>
            </View>
            {shotTypes.map(({ name, value }) => (
              <View key={name} style={styles.distributionRow}>
                <Text style={styles.distributionLabel}>{name}</Text>
                <Text style={styles.distributionValue}>{value}</Text>
                <Text style={styles.distributionPercentage}>
                  {((value / goals.length) * 100).toFixed(1)}%
                </Text>
              </View>
            ))}
          </View>

          <Text style={styles.sectionTitle}>Game Situations Distribution</Text>
          <View style={styles.distributionTable}>
            <View style={[styles.distributionRow, { backgroundColor: '#f3f4f6', paddingVertical: 8 }]}>
              <Text style={[styles.distributionLabel, { fontWeight: 'bold' }]}>Situation</Text>
              <Text style={[styles.distributionValue, { fontWeight: 'bold' }]}>Count</Text>
              <Text style={[styles.distributionPercentage, { fontWeight: 'bold' }]}>Percentage</Text>
            </View>
            {gameSituations.map(({ name, value }) => (
              <View key={name} style={styles.distributionRow}>
                <Text style={styles.distributionLabel}>{name}</Text>
                <Text style={styles.distributionValue}>{value}</Text>
                <Text style={styles.distributionPercentage}>
                  {((value / goals.length) * 100).toFixed(1)}%
                </Text>
              </View>
            ))}
          </View>
        </View>

        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages + gameChunks.length}`} 
          fixed 
        />
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Games Summary</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <View style={[styles.tableCell, styles.col1]}>
                <Text style={styles.tableCellHeader}>Date</Text>
              </View>
              <View style={[styles.tableCell, styles.col2]}>
                <Text style={styles.tableCellHeader}>Opponent</Text>
              </View>
              <View style={[styles.tableCell, styles.col3]}>
                <Text style={styles.tableCellHeader}>Shots</Text>
              </View>
              <View style={[styles.tableCell, styles.col4]}>
                <Text style={styles.tableCellHeader}>Saves</Text>
              </View>
              <View style={[styles.tableCell, styles.col5]}>
                <Text style={styles.tableCellHeader}>Save %</Text>
              </View>
            </View>

            {sortedGames.slice(0, gamesPerPage).map((game) => {
              const gameSavePercentage = game.shotsFaced && game.saves 
                ? ((game.saves / game.shotsFaced) * 100).toFixed(1) 
                : '—';
              
              return (
                <View key={game.id} style={styles.tableRow}>
                  <View style={[styles.tableCell, styles.col1]}>
                    <Text>{game.date.toLocaleDateString()}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col2]}>
                    <Text>{game.opponent}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col3]}>
                    <Text>{game.shotsFaced || '—'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col4]}>
                    <Text>{game.saves || '—'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col5]}>
                    <Text>{gameSavePercentage}%</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages + gameChunks.length}`} 
          fixed 
        />
      </Page>

      {gameChunks.slice(1).map((chunk, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.sectionTitle}>Games Summary (Continued)</Text>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <View style={[styles.tableCell, styles.col1]}>
                <Text style={styles.tableCellHeader}>Date</Text>
              </View>
              <View style={[styles.tableCell, styles.col2]}>
                <Text style={styles.tableCellHeader}>Opponent</Text>
              </View>
              <View style={[styles.tableCell, styles.col3]}>
                <Text style={styles.tableCellHeader}>Shots</Text>
              </View>
              <View style={[styles.tableCell, styles.col4]}>
                <Text style={styles.tableCellHeader}>Saves</Text>
              </View>
              <View style={[styles.tableCell, styles.col5]}>
                <Text style={styles.tableCellHeader}>Save %</Text>
              </View>
            </View>

            {chunk.map((game) => {
              const gameSavePercentage = game.shotsFaced && game.saves 
                ? ((game.saves / game.shotsFaced) * 100).toFixed(1) 
                : '—';
              
              return (
                <View key={game.id} style={styles.tableRow}>
                  <View style={[styles.tableCell, styles.col1]}>
                    <Text>{game.date.toLocaleDateString()}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col2]}>
                    <Text>{game.opponent}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col3]}>
                    <Text>{game.shotsFaced || '—'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col4]}>
                    <Text>{game.saves || '—'}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.col5]}>
                    <Text>{gameSavePercentage}%</Text>
                  </View>
                </View>
              );
            })}
          </View>

          <Text 
            style={styles.pageNumber} 
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages + gameChunks.length}`} 
            fixed 
          />
        </Page>
      ))}
    </Document>
  );
};

export default AnalyticsPDF;